import FileSaver from 'file-saver';
import { format } from 'date-fns';
import ScaffoldSampleValidator from '../scaffold/form/scaffold-sample-validator';
import BuilderService from './builder-service';
import fileReader from '../file/blob-file-item-reader';
import config from '@/config';
import router from '../router';
import entityAndFieldsSorter from './data/entity-and-fields-sorter';
import { v4 as uuid } from 'uuid';
import NodejsFileAdapter from './nodejs-file-adapter';

const clearStoredVersion = (scaffold) => {
  localStorage.removeItem(`${scaffold.id}-version`);
};

const storedVersion = (scaffold) => {
  const val = localStorage.getItem(
    `${scaffold.id}-version`,
  );

  if (!val || val === 'null') {
    return null;
  }

  return val;
};

const storedData = (scaffold) => {
  const val = localStorage.getItem(`${scaffold.id}-data`);

  if (!val || val === 'null') {
    return null;
  }

  return JSON.parse(val);
};

export default {
  namespaced: true,

  state: {
    data: {
      entities: [],
      properties: {},
      relationships: [],
    },
    error: null,
    loading: true,
    loadingBuild: false,
    debugging: config.DEBUG_BUILDER,
    originalProject: null,
    showPreDefined: false,
  },

  getters: {
    originalProject: (state) => state.originalProject,
    showPreDefined: (state) => state.showPreDefined,

    error: (state) => state.error,

    config(state, getters) {
      if (!getters.scaffold) {
        return null;
      }

      return getters.scaffold.config;
    },

    sample(state, getters) {
      if (!getters.scaffold) {
        return null;
      }

      return getters.scaffold.sample;
    },

    existsSavedData(state, getters) {
      if (!getters.scaffold) {
        return false;
      }

      const savedData = storedData(getters.scaffold);
      return (
        savedData &&
        JSON.stringify(savedData).trim() !==
          '{"entities":[],"properties":{},"relationships":[]}'
      );
    },

    existsSample(state, getters) {
      return !!getters.sample;
    },

    loading(state) {
      return !!state.loading;
    },

    loadingBuild(state) {
      return !!state.loadingBuild;
    },

    scaffold(state, getters, rootState, rootGetters) {
      return rootGetters['scaffold/current'];
    },

    data: (state) => state.data,

    debugging(state, getters) {
      return !!state.debugging || getters.simulating;
    },

    simulating(state, getters) {
      return getters.scaffold && getters.scaffold.simulator;
    },
  },

  mutations: {
    error(state, payload) {
      state.error = payload;
    },

    originalProject(state, payload) {
      state.originalProject = payload || null;
    },

    new(state) {
      state.error = null;
      state.data = {
        entities: [],
        properties: {},
        relationships: [],
      };
    },

    loading(state, payload) {
      state.loading = !!payload;
    },

    loadingBuild(state, payload) {
      state.loadingBuild = !!payload;
    },

    loadDataFromFile(state, payload) {
      state.error = null;
      state.data = Object.assign(
        {
          entities: [],
          properties: {},
          relationships: [],
        },
        payload,
      );
    },

    loadDataFromSample(state, sample) {
      if (!sample) {
        throw new Error('Sample is empty!');
      }

      state.error = null;
      state.data = Object.assign(
        {
          entities: [],
          properties: {},
          relationships: [],
        },
        sample,
      );
    },

    loadDataFromProject(state, project) {
      if (!project || !project.schema) {
        throw new Error('Schema is empty!');
      }

      state.error = null;
      state.data = Object.assign(
        {
          entities: [],
          properties: {},
          relationships: [],
        },
        JSON.parse(project.schema),
      );
    },

    loadDataFromLocalStorage(state, scaffold) {
      if (!scaffold || !scaffold.id) {
        throw new Error('Scaffold is empty!');
      }

      if (storedData(scaffold)) {
        state.error = null;
        state.data = Object.assign(
          {
            entities: [],
            properties: {},
            relationships: [],
          },
          storedData(scaffold),
        );
      }
    },

    persistStateToLocalStorage(state, scaffold) {
      if (!scaffold || !scaffold.id) {
        throw new Error('Scaffold is empty!');
      }

      localStorage.setItem(
        `${scaffold.id}-version`,
        scaffold.version,
      );
      localStorage.setItem(
        `${scaffold.id}-data`,
        JSON.stringify(state.data),
      );
    },

    mergePropertiesDefaults(state, defaults) {
      state.data.properties = Object.assign(
        defaults,
        state.data.properties,
      );
    },

    toggleShowPreDefined(state) {
      state.showPreDefined = !state.showPreDefined;
    },

    applyFrontend(state, payload) {
      state.data = state.data || {};
      state.data.properties = state.data.properties || {};
      state.data.properties.frontend = payload;
    },
  },

  actions: {
    toggleShowPreDefined({ commit }) {
      commit('toggleShowPreDefined');
    },

    clearError({ commit }) {
      commit('error', null);
    },

    persistStateToLocalStorage({ commit, getters }) {
      commit(
        'persistStateToLocalStorage',
        getters.scaffold,
      );
    },

    async build({ dispatch, commit, getters, state }) {
      commit('loadingBuild', true);
      commit('error', null);

      try {
        await dispatch('validate');
      } catch (error) {
        commit('loadingBuild', false);
        return;
      }

      try {
        const projectId = await BuilderService.build({
          scaffold: getters.scaffold,
          schema: state.data,
          originalProject: getters.originalProject,
        });

        router.push({
          name: 'project-preview',
          params: {
            projectId,
          },
        });

        commit('loadingBuild', false);
      } catch (error) {
        if (error && error.message) {
          let errorHeader = 'Error';

          if (error.code === 400) {
            errorHeader = 'Fix the following problems';
          }

          if (error.code === 429) {
            errorHeader = 'Sorry, too many projects';
          }

          commit('error', {
            message: error.message,
            header: errorHeader,
          });
        } else {
          dispatch(
            'core/validationError',
            new Error(
              'Ops, an error occurred! Please try again later.',
            ),
            {
              root: true,
            },
          );
        }
        commit('loadingBuild', false);
      }
    },

    async validate({ rootGetters, dispatch, state }) {
      const isPropertyValid = await rootGetters[
        'builder/propertyCurrent/validate'
      ]();

      if (!isPropertyValid) {
        throw new Error();
      }

      const entitiesEnabled =
        rootGetters['builder/entity/enabled'];

      if (!entitiesEnabled) {
        return;
      }

      if (
        !state.data.entities ||
        !state.data.entities.length
      ) {
        dispatch(
          'core/validationError',
          new Error(
            'At least one custom entity is required',
          ),
          { root: true },
        );

        throw new Error();
      }
    },

    async setup({ getters, dispatch, commit }, payload) {
      try {
        const {
          scaffoldId,
          version,
          reload,
          originalProject,
          frontend,
        } = payload;

        const settedUp =
          getters.scaffold &&
          getters.scaffold.id === scaffoldId &&
          (!version ||
            getters.scaffold.version === version) &&
          !reload;

        if (settedUp) {
          return;
        }

        commit('loading', true);

        await dispatch(
          'scaffold/load',
          { id: scaffoldId, version: version },
          {
            root: true,
          },
        );

        if (originalProject) {
          await dispatch(
            'loadDataFromProject',
            originalProject,
          );
        } else if (getters.existsSavedData) {
          await dispatch('loadStateFromLocalStorage', {
            version,
          });
        } else if (getters.existsSample) {
          await dispatch('loadDataFromSample');
        }

        if (frontend) {
          commit('applyFrontend', frontend);
        }

        commit('loading', false);
      } catch (error) {
        commit('loading', false);
        dispatch('core/error', error, { root: true });
        throw error;
      }
    },

    simulate({ commit, dispatch }, payload) {
      commit(
        'scaffold/current',
        {
          id: uuid(),
          url: 'simulator',
          name: 'Simulator',
          header: 'Simulator',
          simulator: true,
          config: payload,
          sample: null,
        },
        { root: true },
      );

      commit('loading', false);
      return dispatch('newProject');
    },

    async loadVersionAndNewProject({ dispatch }, version) {
      await dispatch('loadVersion', version);
      return dispatch('newProject');
    },

    async loadVersion(
      { commit, dispatch, rootGetters },
      version,
    ) {
      const scaffold = rootGetters['scaffold/current'];

      if (scaffold.version === version) {
        return;
      }

      try {
        commit('loading', true);

        await dispatch(
          'scaffold/load',
          {
            id: scaffold.id,
            version,
          },
          {
            root: true,
          },
        );

        commit('loading', false);
      } catch (error) {
        clearStoredVersion(scaffold);
        commit('loading', false);
        dispatch('core/error', error, { root: true });
        throw error;
      }
    },

    async loadDataFromProject(
      { state, commit, dispatch },
      payload,
    ) {
      try {
        commit('originalProject', payload);
        commit('loadDataFromProject', payload);

        commit('builder/entityCurrent/clear', null, {
          root: true,
        });
        commit('builder/fieldCurrent/clear', null, {
          root: true,
        });
        commit(
          'builder/fieldValidationCurrent/clear',
          null,
          { root: true },
        );
        commit(
          'builder/entityCurrent/bind',
          state.data.entities,
          { root: true },
        );
        commit(
          'builder/relationshipCurrent/bind',
          state.data.relationships,
          { root: true },
        );
        return dispatch('persistStateToLocalStorage');
      } catch (error) {
        dispatch('core/error', error, { root: true });
      }
    },

    loadDataFromFile(
      { state, commit, dispatch, getters },
      payload,
    ) {
      if (!payload || !payload.length) {
        return Promise.resolve();
      }

      commit('originalProject', null);
      const file = payload[0];
      const readableFile = {
        blob: new Blob([file]),
      };

      return fileReader
        .read(readableFile)
        .then(() => {
          let data = JSON.parse(readableFile.content);

          if (getters.scaffold.id === 'nodejs') {
            data = NodejsFileAdapter.adapt(data);
          }

          new ScaffoldSampleValidator(
            data,
            getters.config,
          ).validate();
          commit('loadDataFromFile', data);
          commit('builder/entityCurrent/clear', null, {
            root: true,
          });
          commit('builder/fieldCurrent/clear', null, {
            root: true,
          });
          commit(
            'builder/fieldValidationCurrent/clear',
            null,
            { root: true },
          );
          commit(
            'builder/entityCurrent/bind',
            state.data.entities,
            { root: true },
          );
          commit(
            'builder/relationshipCurrent/bind',
            state.data.relationships,
            { root: true },
          );
          return dispatch('persistStateToLocalStorage');
        })
        .catch((error) =>
          dispatch('core/error', error, { root: true }),
        );
    },

    loadDataFromSample({
      state,
      commit,
      dispatch,
      getters,
    }) {
      commit('originalProject', null);

      commit('builder/entityCurrent/clear', null, {
        root: true,
      });
      commit('builder/fieldCurrent/clear', null, {
        root: true,
      });
      commit('builder/fieldValidationCurrent/clear', null, {
        root: true,
      });
      commit('loadDataFromSample', getters.sample);
      commit(
        'builder/entityCurrent/bind',
        state.data.entities,
        { root: true },
      );
      commit(
        'builder/relationshipCurrent/bind',
        state.data.relationships,
        { root: true },
      );
      dispatch('persistStateToLocalStorage');
    },

    loadStateFromLocalStorage(
      { state, commit, dispatch, rootGetters },
      { version },
    ) {
      commit('originalProject', null);

      commit('builder/entityCurrent/clear', null, {
        root: true,
      });
      commit('builder/fieldCurrent/clear', null, {
        root: true,
      });
      commit('builder/fieldValidationCurrent/clear', null, {
        root: true,
      });
      commit(
        'loadDataFromLocalStorage',
        rootGetters['scaffold/current'],
      );
      commit(
        'builder/entityCurrent/bind',
        state.data.entities,
        { root: true },
      );
      commit(
        'builder/relationshipCurrent/bind',
        state.data.relationships,
        { root: true },
      );

      const scaffold = rootGetters['scaffold/current'];

      if (!version && storedVersion(scaffold)) {
        return dispatch(
          'loadVersion',
          storedVersion(scaffold),
        );
      }

      return Promise.resolve();
    },

    newProject({ state, commit, dispatch, rootGetters }) {
      commit('originalProject', null);

      commit('builder/entityCurrent/clear', null, {
        root: true,
      });
      commit('builder/fieldCurrent/clear', null, {
        root: true,
      });
      commit('builder/fieldValidationCurrent/clear', null, {
        root: true,
      });
      commit('new');
      commit(
        'mergePropertiesDefaults',
        rootGetters['builder/property/defaults'],
      );
      commit(
        'builder/entityCurrent/bind',
        state.data.entities,
        { root: true },
      );
      commit(
        'builder/relationshipCurrent/bind',
        state.data.relationships,
        { root: true },
      );
      dispatch('persistStateToLocalStorage');
    },

    downloadData({ state, getters }, payload) {
      let filename = payload;

      if (!filename) {
        const timestamp = format(
          new Date(),
          'yyyyMMddHHmmss',
        );
        const { id } = getters.scaffold;
        filename = `${timestamp}-${id}.json`;
      }

      // Sort entity and fields to export the data
      entityAndFieldsSorter(state.data.entities);

      FileSaver.saveAs(
        new Blob([JSON.stringify(state.data)]),
        filename,
      );
    },
  },
};
